// Shared utilities for the calendar islands (ScheduleGrid, ByCoachGrid,
// ClassBulkAddGrid, AvailabilityGrid) and ShiftDetailModal. Anything
// genuinely identical across them lives here; cosmetic per-surface
// choices (time-formatting strings, weekday picker math) stay in the
// island that owns the visual decision.

export const pad = (n: number): string => String(n).padStart(2, "0")

export const WEEKDAY_LABEL = [ "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat" ] as const

export const FALLBACK_COLOR = "#b4347e"
export const DRAFT_COLOR    = "#9ca3af"

export type WallClock = { y: number, mo: number, d: number, h: number, mi: number }

// Pull wall-clock parts straight out of an ISO 8601 string —
// "2026-05-12T19:00:00-04:00" → { y: 2026, mo: 5, d: 12, h: 19, mi: 0 }.
// The server emits times already wall-clocked to the app TZ, so the digits
// before the offset are what we want to display. Going through `new Date()`
// would re-interpret to the viewer's TZ — wrong for our app.
export function wallClockFromIso(iso: string): WallClock {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso)
  if (!m) return { y: 0, mo: 1, d: 1, h: 0, mi: 0 }
  return { y: +m[1], mo: +m[2], d: +m[3], h: +m[4], mi: +m[5] }
}

// Parse "#rrggbb" → [r, g, b]; falls back to neutral grey on garbage.
export function hexToRgb(hex: string): [ number, number, number ] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex)
  if (!m) return [ 156, 163, 175 ]
  const n = parseInt(m[1], 16)
  return [ (n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff ]
}

export function hexToRgba(hex: string, alpha: number): string {
  const [ r, g, b ] = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// Pick a readable text colour for a tinted-fill block.
export function readableTextOn(hex: string): string {
  const [ r, g, b ] = hexToRgb(hex)
  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
  return lum > 0.6 ? "#111827" : "#1f2937"
}

// Client-side toast — appends a daisyUI .alert node to a fixed .toast
// container (creating the container if the page didn't render one). The
// `toast` Stimulus controller already handles auto-dismiss + click-to-close,
// so we just have to drop a properly-attributed node in. Mirrors the
// server-rendered flash markup in app/views/layouts/application.html.erb.
type ToastLevel = "notice" | "alert" | "warning" | "info"
export function showToast(opts: { level?: ToastLevel; message: string; delayMs?: number }) {
  const level = opts.level ?? "info"
  const { alertClass, delay } =
    level === "alert"   ? { alertClass: "alert-error",   delay: 0    } :
    level === "notice"  ? { alertClass: "alert-success", delay: 4000 } :
    level === "warning" ? { alertClass: "alert-warning", delay: 6000 } :
                          { alertClass: "alert-info",    delay: 4000 }

  let container = document.querySelector(".toast.toast-end") as HTMLElement | null
  if (!container) {
    container = document.createElement("div")
    container.setAttribute("aria-live", "polite")
    container.className = "toast toast-end z-50 inset-x-4 left-4 sm:left-auto sm:inset-x-auto pb-[env(safe-area-inset-bottom,0px)]"
    document.body.appendChild(container)
  }

  const node = document.createElement("div")
  node.setAttribute("role", "alert")
  node.className = `alert ${alertClass} shadow-lg transition-all duration-200 ease-out`
  node.setAttribute("data-controller", "toast")
  node.setAttribute("data-toast-delay-value", String(opts.delayMs ?? delay))

  const span = document.createElement("span")
  span.textContent = opts.message
  node.appendChild(span)

  const btn = document.createElement("button")
  btn.type = "button"
  btn.className = "btn btn-xs btn-ghost btn-circle"
  btn.setAttribute("data-action", "toast#dismiss")
  btn.setAttribute("aria-label", "Dismiss")
  btn.textContent = "×"
  node.appendChild(btn)

  container.appendChild(node)
}

// `showToast` flashes a toast that vanishes if the page reloads. Use
// `queueToast` when you're about to call `window.location.reload()`; the
// payload is stashed in sessionStorage and replayed on the next page load
// by `drainPendingToasts` (wired up in entrypoints/application.ts).
const PENDING_TOASTS_KEY = "whentocircus:pending-toasts"

type ToastPayload = { level: ToastLevel; message: string; delayMs?: number }

export function queueToast(payload: ToastPayload) {
  try {
    const raw = sessionStorage.getItem(PENDING_TOASTS_KEY)
    const arr: ToastPayload[] = raw ? JSON.parse(raw) : []
    arr.push(payload)
    sessionStorage.setItem(PENDING_TOASTS_KEY, JSON.stringify(arr))
  } catch {
    // sessionStorage failure (Safari private mode etc.) — fall back to
    // showing the toast immediately; the reload will eat it but better
    // that than silent.
    showToast(payload)
  }
}

export function drainPendingToasts() {
  let arr: ToastPayload[] = []
  try {
    const raw = sessionStorage.getItem(PENDING_TOASTS_KEY)
    if (!raw) return
    arr = JSON.parse(raw)
    sessionStorage.removeItem(PENDING_TOASTS_KEY)
  } catch {
    return
  }
  for (const t of arr) showToast(t)
}

// Calendar toolbars do a full navigation when the user clicks Prev/Next
// — the browser scrolls back to the top, which is jarring if the user
// was halfway down the page (e.g. looking at a specific shift on the
// solver-run review). Stash the scroll position before navigating and
// restore it on the next load if we're still on the same path.
const PENDING_SCROLL_KEY = "whentocircus:pending-scroll"
const PENDING_SCROLL_MAX_AGE_MS = 30_000

export function navigateWithScrollPreserved(url: string) {
  try {
    sessionStorage.setItem(PENDING_SCROLL_KEY, JSON.stringify({
      pathname: window.location.pathname,
      y:        window.scrollY,
      t:        Date.now(),
    }))
  } catch {
    // sessionStorage failure — fall through; we just lose scroll preservation.
  }
  window.location.assign(url)
}

export function restorePendingScroll() {
  let entry: { pathname: string; y: number; t: number } | null = null
  try {
    const raw = sessionStorage.getItem(PENDING_SCROLL_KEY)
    if (!raw) return
    entry = JSON.parse(raw)
    sessionStorage.removeItem(PENDING_SCROLL_KEY)
  } catch {
    return
  }
  if (!entry) return
  if (Date.now() - entry.t > PENDING_SCROLL_MAX_AGE_MS) return
  if (entry.pathname !== window.location.pathname) return

  // React islands mount asynchronously (dynamic import → render), so on the
  // first frame after DOMContentLoaded the body is often shorter than the
  // target scrollY. scrollTo(0, target) silently clamps to max-scroll in
  // that case. Keep nudging on each animation frame until the document is
  // tall enough to actually land us at the target, or until we give up.
  // Two-arg scrollTo is always instant — avoids fighting any smooth-scroll
  // CSS on the page.
  const target = entry.y
  let frames = 0
  const tryScroll = () => {
    window.scrollTo(0, target)
    if (window.scrollY < target - 1 && frames < 90) { // ~1.5s @ 60fps
      frames += 1
      requestAnimationFrame(tryScroll)
    }
  }
  requestAnimationFrame(tryScroll)
}
